import { computed, ref, toRef, type MaybeRef } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import siteService from '@/services/client/site'
import { Site, type ISiteData, type ISite } from '@/models/client'
import { useAuthStore, useSnackbarStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { toSiteCreatePayload, toSiteUpdatePayload } from '@/models/client'

/** Create mutation payload: domain or DTO for the new site. */
export interface ICreateSitePayload {
  clientId?: number
  site: Partial<ISite> | Partial<ISiteData>
}

export function useFetchSites(clientId: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)

  const authStore = useAuthStore()
  const { getUser: currentUser } = storeToRefs(authStore)

  const queryKey = ref(['sites', clientIdRef])

  const {
    isLoading,
    isError,
    error,
    data: sites
  } = useQuery({
    // To be able to call sites it must have a client and the permission
    enabled: computed(() =>
      Boolean(currentUser.value?.hasPermission('clients.view_site') && clientIdRef.value)
    ),
    queryKey: queryKey,
    queryFn: () => siteService.fetchSites(clientIdRef.value),

    select: (data) => {
      return data.map((site) => new Site(site))
    }
  })

  return { queryKey, sites, error, isError, isLoading }
}

export function useFetchSite(clientId: number, siteId: MaybeRef<number>) {
  const siteIdRef = toRef(siteId)

  const queryKey = ref(['sites', clientId, siteIdRef])

  const {
    data: site,
    isPending,
    error
  } = useQuery({
    queryKey: queryKey,
    queryFn: () => siteService.fetchSite(clientId, siteIdRef.value),
    select: (data) => new Site(data)
  })

  return { site, isPending, error, queryKey }
}

export function useCreateSite(clientId?: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)

  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['create-site'],
    mutationFn: (payload: ICreateSitePayload) =>
      siteService.createSite(clientIdRef.value || payload.clientId!, toSiteCreatePayload(payload.site)),

    onSuccess: (data) => {
      queryClient.setQueryData(['site', data.code], data)
      queryClient.invalidateQueries({ queryKey: ref(['sites', clientId]) })
      snackbarStore.showSnackbar('Site created successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useUpdateSite(clientId: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)

  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, mutateAsync, reset } = useMutation({
    mutationKey: ['update-site'],
    mutationFn: (site: Partial<ISite> | Partial<ISiteData>) =>
      siteService.updateSite(clientIdRef.value, toSiteUpdatePayload(site)),

    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ref(['site', data.code]) })
      queryClient.invalidateQueries({ queryKey: ref(['sites', clientId]) })
      snackbarStore.showSnackbar('Site updated successfully')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    mutateAsync,
    reset
  }
}

export function useDeleteSite(clientId: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)

  const snackbarStore = useSnackbarStore()
  const queryClient = useQueryClient()

  const { isPending, isError, error, isSuccess, mutate, reset } = useMutation({
    mutationKey: ref(['delete-site']),
    mutationFn: (id: number) => siteService.deleteSite(clientIdRef.value, id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ref(['sites', clientId]) })
      snackbarStore.showSnackbar('Site deleted successfully', '')
    }
  })
  return {
    isPending,
    isError,
    error,
    isSuccess,
    mutate,
    reset
  }
}

const GEOCODE_POLL_INTERVAL_MS = 2500
const GEOCODE_POLL_TIMEOUT_MS = 60000

export function useTriggerSiteGeocode(clientId: MaybeRef<number>, siteCode: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)
  const siteCodeRef = toRef(siteCode)
  const snackbarStore = useSnackbarStore()

  const { isPending, mutateAsync, reset } = useMutation({
    mutationKey: ['trigger-site-geocode'],
    mutationFn: (force?: boolean) =>
      siteService.triggerGeocode(clientIdRef.value, siteCodeRef.value, force),
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        snackbarStore.showSnackbar(
          'Site has manual location; use Regenerate with overwrite to replace with geocoding.',
          'error'
        )
      } else {
        snackbarStore.showSnackbar('Failed to trigger geocoding', 'error')
      }
    }
  })
  return { isPending, triggerGeocode: mutateAsync, reset }
}

export function useSetSiteLocation(clientId: MaybeRef<number>, siteCode: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)
  const siteCodeRef = toRef(siteCode)
  const queryClient = useQueryClient()

  const { isPending, mutateAsync, reset } = useMutation({
    mutationKey: ['set-site-location'],
    mutationFn: ({ lat, lng }: { lat: number; lng: number }) =>
      siteService.setLocation(clientIdRef.value, siteCodeRef.value, lat, lng),
    onSuccess: (data) => {
      queryClient.setQueryData(['sites', clientIdRef.value, data.code], data)
      queryClient.invalidateQueries({ queryKey: ref(['sites', clientIdRef.value]) })
    }
  })
  return { isPending, setLocation: mutateAsync, reset }
}

export async function pollSiteUntilGeocodeDone(
  clientId: number,
  siteCode: number
): Promise<ISiteData> {
  const start = Date.now()
  for (;;) {
    const site = await siteService.fetchSite(clientId, siteCode)
    const status = site.geocode_status
    if (status === 'ok' || status === 'failed') {
      return site
    }
    if (Date.now() - start >= GEOCODE_POLL_TIMEOUT_MS) {
      return site
    }
    await new Promise((r) => setTimeout(r, GEOCODE_POLL_INTERVAL_MS))
  }
}

export function useSiteGeocodeWithPolling(clientId: MaybeRef<number>, siteCode: MaybeRef<number>) {
  const clientIdRef = toRef(clientId)
  const siteCodeRef = toRef(siteCode)
  const queryClient = useQueryClient()
  const isPolling = ref(false)
  const geocodeStatus = ref<ISiteData['geocode_status']>(undefined)

  const { triggerGeocode, isPending: isTriggerPending } = useTriggerSiteGeocode(
    clientIdRef,
    siteCodeRef
  )

  async function triggerAndPoll(force?: boolean) {
    isPolling.value = true
    geocodeStatus.value = 'pending'
    try {
      await triggerGeocode(force)
      const site = await pollSiteUntilGeocodeDone(clientIdRef.value, siteCodeRef.value)
      geocodeStatus.value = site.geocode_status
      queryClient.setQueryData(['sites', clientIdRef.value, site.code], site)
      queryClient.invalidateQueries({ queryKey: ref(['sites', clientIdRef.value]) })
      return site
    } catch (e) {
      geocodeStatus.value = 'failed'
      throw e
    } finally {
      isPolling.value = false
    }
  }

  return {
    isPolling,
    geocodeStatus,
    isPending: isTriggerPending,
    triggerAndPoll
  }
}
